import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const PartnerCalc = () => {
  useEffect(() => {
    document.title = "System Performance Optimizer - Hyperapt";
    const m = document.querySelector('meta[name="description"]');
    const descText = "Hyperapt Performance and Operational Flow Yield Estimation Portal";
    if (m) {
      m.setAttribute("content", descText);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = descText;
      document.head.appendChild(meta);
    }
    window.scrollTo(0, 0);
  }, []);

  // OPD equivalent: Daily Volume Index (range 0 to 200)
  const [flowIndex, setFlowIndex] = useState(20);
  // AOV equivalent: Unit Value Quotient (range 100 to 5000)
  const [valueQuotient, setValueQuotient] = useState(1000);
  // Projection period: 'monthly' or 'yearly'
  const [period, setPeriod] = useState("yearly");
  // Optional monthly cycle days (default 25)
  const [workingDays, setWorkingDays] = useState(25);
  // Toggle visibility of optional cycle settings
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Multiplier for projection
  const multiplier = period === "yearly" ? workingDays * 12 : workingDays;

  // Margin indices
  const tiers = [
    {
      name: "Tier I Resource Yield",
      description: "Baseline operational support allocation",
      factor: 0.05,
      bg: "bg-white/80 border-web-border",
      textColor: "text-web-text/90",
      accentColor: "bg-web-secondary",
      isDark: false,
    },
    {
      name: "Tier II Resource Yield",
      description: "Balanced system support allocation",
      factor: 0.15,
      bg: "bg-white border-web-primary/10 shadow-lg shadow-web-primary/5",
      textColor: "text-web-primary",
      accentColor: "bg-web-primary",
      featured: true,
      isDark: false,
    },
    {
      name: "Tier III Resource Yield",
      description: "Enhanced velocity system support allocation",
      factor: 0.25,
      bg: "bg-[#1E1C1A] text-white border-white/10 shadow-xl shadow-black/10",
      textColor: "text-web-secondary",
      accentColor: "bg-web-secondary",
      isDark: true,
    },
  ];

  // Helper function to format currency
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  // Percentages for styling standard ranges
  const flowPercentage = (flowIndex / 200) * 100;
  const valuePercentage = ((valueQuotient - 100) / 4900) * 100;

  return (
    <div className="min-h-screen bg-web-bg text-web-text font-web-sans flex flex-col selection:bg-web-primary selection:text-white overflow-x-hidden">
      {/* Header - Matching Partner.js layout */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md transition-all duration-300 h-20 flex items-center border-b border-web-border/50">
        <div className="max-w-7xl mx-auto px-4 w-full flex items-center justify-center">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-semibold text-[36px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>
        </div>
      </header>

      <main className="flex-1 w-full relative pt-20">
        {/* Decorative Background Elements */}
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-web-secondary/15 rounded-full blur-[100px] -z-10 pointer-events-none" />
        <div className="absolute bottom-20 left-0 w-[600px] h-[600px] bg-web-primary/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

        {/* Hero Banner / Introductory Section */}
        <section className="relative w-full max-w-4xl mx-auto px-4 pt-12 pb-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-1.5 rounded-full bg-web-primary/10 text-web-primary font-semibold text-xs tracking-wider uppercase mb-4">
              System Efficiency Modeling
            </div>
            <h1 className="font-web-serif text-4xl md:text-5xl font-bold leading-tight mb-4">
              Practice Flow & Resource Sandbox
            </h1>
            <p className="text-base text-web-text/60 leading-relaxed max-w-xl mx-auto font-light">
              Model daily consultation dispatch capacity to estimate system resource allocations and support yields.
            </p>
          </motion.div>
        </section>

        {/* Calculator Widget Wrapper */}
        <section className="w-full max-w-5xl mx-auto px-4 pb-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="bg-white/40 backdrop-blur-xl p-6 md:p-10 rounded-[32px] border border-white shadow-xl shadow-black/[0.02] flex flex-col gap-8"
          >
            {/* Toggle controls - Monthly vs Yearly */}
            <div className="flex justify-center">
              <div className="relative bg-web-secondary/20 p-1.5 rounded-full flex gap-1 border border-web-border">
                <button
                  onClick={() => setPeriod("monthly")}
                  className={`relative z-10 px-6 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${period === "monthly" ? "text-web-text shadow-sm" : "text-web-text/50 hover:text-web-text"
                    }`}
                >
                  Monthly Cycle
                  {period === "monthly" && (
                    <motion.div
                      layoutId="activePeriod"
                      className="absolute inset-0 bg-white rounded-full -z-10 shadow-sm border border-web-border/30"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
                <button
                  onClick={() => setPeriod("yearly")}
                  className={`relative z-10 px-6 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${period === "yearly" ? "text-web-text shadow-sm" : "text-web-text/50 hover:text-web-text"
                    }`}
                >
                  Yearly Output
                  {period === "yearly" && (
                    <motion.div
                      layoutId="activePeriod"
                      className="absolute inset-0 bg-white rounded-full -z-10 shadow-sm border border-web-border/30"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </button>
              </div>
            </div>

            {/* Input Slider Controls */}
            <div className="grid md:grid-cols-2 gap-12">
              {/* Slider 1: Flow Index (OPD) */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm tracking-wide text-web-text/80 uppercase">
                      Daily OPD Consultation Flow
                    </span>
                    <span className="text-xs text-web-text/40">
                      Average daily clinic consultation count (OPD)
                    </span>
                  </div>
                  <div className="bg-web-primary/5 border border-web-primary/10 rounded-2xl px-4 py-1">
                    <span className="font-web-serif text-2xl font-bold text-web-primary">
                      {flowIndex}
                    </span>
                  </div>
                </div>

                <div className="relative mt-4 flex items-center">
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={flowIndex}
                    onChange={(e) => setFlowIndex(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none"
                    style={{
                      background: `linear-gradient(to right, #D13438 0%, #D13438 ${flowPercentage}%, #EAE4D9 ${flowPercentage}%, #EAE4D9 100%)`,
                    }}
                  />
                  <style>{`
                    input[type='range']::-webkit-slider-thumb {
                      -webkit-appearance: none;
                      appearance: none;
                      width: 24px;
                      height: 24px;
                      border-radius: 50%;
                      background: #D13438;
                      border: 4px solid #ffffff;
                      box-shadow: 0 4px 10px rgba(209, 52, 56, 0.4);
                      cursor: pointer;
                      transition: transform 0.1s ease;
                    }
                    input[type='range']::-webkit-slider-thumb:hover {
                      transform: scale(1.15);
                    }
                  `}</style>
                </div>
                <div className="flex justify-between text-xs text-web-text/30 px-1">
                  <span>Min: 0</span>
                  <span>Max: 200</span>
                </div>
              </div>

              {/* Slider 2: Value Quotient (AOV) */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm tracking-wide text-web-text/80 uppercase">
                      Mean Prescription Value Index
                    </span>
                    <span className="text-xs text-web-text/40">
                      Estimated average value of medications per rx
                    </span>
                  </div>
                  <div className="bg-web-primary/5 border border-web-primary/10 rounded-2xl px-4 py-1">
                    <span className="font-web-serif text-2xl font-bold text-web-primary">
                      {formatCurrency(valueQuotient)}
                    </span>
                  </div>
                </div>

                <div className="relative mt-4 flex items-center">
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="50"
                    value={valueQuotient}
                    onChange={(e) => setValueQuotient(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none"
                    style={{
                      background: `linear-gradient(to right, #D13438 0%, #D13438 ${valuePercentage}%, #EAE4D9 ${valuePercentage}%, #EAE4D9 100%)`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-web-text/30 px-1">
                  <span>Min: {formatCurrency(100)}</span>
                  <span>Max: {formatCurrency(5000)}</span>
                </div>
              </div>
            </div>

            {/* Optional Settings Collapsible section */}
            <div className="flex flex-col items-center justify-center gap-2 border-t border-web-border/50 pt-4">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-web-primary font-semibold uppercase tracking-wider hover:opacity-80 flex items-center gap-1.5 transition-opacity"
              >
                <span>{showAdvanced ? "Hide" : "Show"} Operational Days Setting</span>
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-300 ${showAdvanced ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="w-full max-w-sm flex flex-col gap-3 mt-2 px-5 py-4 rounded-2xl bg-web-secondary/10 border border-web-border/60 overflow-hidden"
                  >
                    <div className="flex items-center justify-between text-xs font-semibold text-web-text/70 uppercase">
                      <span>Monthly Active Practice Days</span>
                      <span className="font-web-serif text-sm font-bold text-web-primary">{workingDays} Days</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="31"
                      value={workingDays}
                      onChange={(e) => setWorkingDays(Number(e.target.value))}
                      className="w-full h-1 bg-web-secondary rounded-lg appearance-none cursor-pointer accent-web-primary focus:outline-none"
                    />
                    <div className="flex justify-between text-[9px] text-web-text/30">
                      <span>Min: 10</span>
                      <span>Default: 25</span>
                      <span>Max: 31</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Yield Results Display Grid */}
            <div className="mt-4 flex flex-col gap-6">
              <h3 className="text-center font-web-serif text-2xl font-bold tracking-tight">
                Estimated System Resource Yields
              </h3>

              <div className="grid md:grid-cols-3 gap-6">
                {tiers.map((tier, idx) => {
                  const calculatedYield = flowIndex * valueQuotient * tier.factor * multiplier;

                  return (
                    <motion.div
                      key={idx}
                      whileHover={{ y: -6, scale: 1.01 }}
                      className={`relative flex flex-col p-6 rounded-3xl border transition-all duration-300 ${tier.bg}`}
                    >
                      {tier.featured && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-web-primary text-white text-[10px] uppercase font-bold tracking-widest rounded-full shadow-md">
                          Balanced Optimal
                        </div>
                      )}

                      <div className="flex-1 flex flex-col gap-2">
                        <span className={`font-semibold text-xs tracking-wider uppercase ${tier.isDark ? "text-web-secondary/80" : "text-web-text/50"}`}>
                          {tier.name}
                        </span>
                        <p className={`text-xs font-light leading-relaxed ${tier.isDark ? "text-white/70" : "text-web-text/60"}`}>
                          {tier.description}
                        </p>
                      </div>

                      <div className="mt-8 flex flex-col gap-1">
                        <span className={`text-[10px] uppercase font-semibold tracking-wider ${tier.isDark ? "text-white/40" : "text-web-text/30"}`}>
                          Fulfillment resource index
                        </span>
                        <span className={`font-web-serif text-3xl font-bold tracking-tight ${tier.textColor}`}>
                          {formatCurrency(calculatedYield)}
                        </span>
                        <span className={`text-[10px] italic ${tier.isDark ? "text-white/50" : "text-web-text/40"}`}>
                          Projected per {period === "yearly" ? "annual cycle" : "monthly cycle"}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* General System Footnote */}
            <div className="mt-4 border-t border-web-border/60 pt-6 text-center text-xs text-web-text/40 leading-relaxed font-light max-w-xl mx-auto">
              Projections are illustrative estimations of system resource allocations based on fulfillment volume profiles. Actual allocations depend on dispatch success rates, inventory logistics, and partner location metrics.
            </div>
          </motion.div>
        </section>
      </main>

      {/* Footer - Identical to Partner.js */}
      <footer className="bg-web-bg text-web-text py-8 border-t border-web-border mt-auto relative z-30">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/home" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto opacity-90" />
            <span className="font-web-serif font-semibold text-[32px] tracking-normal text-web-text">
              hyperapt
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-6 text-sm text-web-text/70 font-light">
            <Link to="/trackorder" className="hover:text-web-primary transition-colors">Track Order</Link>
            <Link to="/partner" className="hover:text-web-primary transition-colors">For Doctors</Link>
            <Link to="/terms" className="hover:text-web-primary transition-colors">Terms of Service</Link>
            <Link to="/privacypolicy" className="hover:text-web-primary transition-colors">Privacy Policy</Link>
            <Link to="/returnpolicy" className="hover:text-web-primary transition-colors">Return Policy</Link>
          </nav>

          <div className="text-sm text-web-text/50 font-light">
            © {new Date().getFullYear()} Hyperapt. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerCalc;
